"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const focalTestimonials = [{
  quote: "Catalyst Growth Lab delivers a high-velocity",
  highlight: "performance engine that scales revenue",
  quoteEnd: "with surgical precision and data-driven speed.",
  name: "Sarah Jenkins",
  role: "CMO, TechFlow",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_sarah.webp",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_sarah.webp-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_sarah.webp-wvc-sizes"
}, {
  quote: "The strategic chemistry at Catalyst is",
  highlight: "truly disruptive, transforming brands",
  quoteEnd: "into market leaders through sheer digital force.",
  name: "Marcus Thorne",
  role: "Founder, Velocity AI",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_marcus.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_marcus.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_marcus.png-wvc-sizes"
}, {
  quote: "Every campaign feels like a calculated strike.",
  highlight: "Catalyst has redefined our ROI",
  quoteEnd: "in the competitive digital landscape.",
  name: "Elena Rodriguez",
  role: "VP Growth, Nexa",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_elena.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_elena.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_elena.png-wvc-sizes"
}, {
  quote: "An agency that pairs raw data with artistry.",
  highlight: "Every detail is intentional",
  quoteEnd: "and built for maximum conversion.",
  name: "Theo Vance",
  role: "Director, Peak Digital",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_theo.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_theo.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_theo.png-wvc-sizes"
}];
const smallTestimonials = [{
  quote: "A high-energy partner that turns complex data into clear, profitable growth.",
  name: "Clara Chen",
  role: "Founder, Zenith",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_clara.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_clara.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_clara.png-wvc-sizes"
}, {
  quote: "Each sprint feels like discovering a new chapter of digital dominance.",
  name: "Daniel Kim",
  role: "CEO, Flux Media",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_daniel.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_daniel.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_daniel.png-wvc-sizes"
}, {
  quote: "Aggressive, refined, and results-obsessed. My go-to growth partner.",
  name: "Mira Osei",
  role: "Head of Growth, Orbit",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_mira.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_mira.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/client_mira.png-wvc-sizes"
}];
export default function ClientEchoes() {
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const active = focalTestimonials[activeIndex];
  const handlePrev = () => {
    setActiveIndex(prev => prev === 0 ? focalTestimonials.length - 1 : prev - 1);
  };
  const handleNext = () => {
    setActiveIndex(prev => prev === focalTestimonials.length - 1 ? 0 : prev + 1);
  };
  const fullQuote = `${active.quote} ${active.highlight} ${active.quoteEnd}`;
  const words = fullQuote.split(" ");
  const highlightWords = active.highlight.split(" ");
  const highlightStart = active.quote.split(" ").length;
  const highlightEnd = highlightStart + highlightWords.length;
  return <section data-section-id="4213" id="client-echoes" className="bg-background text-foreground py-24 md:py-32 lg:py-[140px] overflow-hidden">
      <div ref={sectionRef} className="container mx-auto px-6 md:px-10 lg:px-16">
        {}
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-3">
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-primary">
              — 06 // IMPACT
            </span>
          </div>
        </div>

        {}
        <div className="grid grid-cols-12 gap-6 relative mb-24 md:mb-32 lg:mb-40">
          <div className="col-span-12 md:col-start-3 md:col-span-8 relative">
            {}
            <span aria-hidden="true" className="absolute -top-16 md:-top-20 lg:-top-24 -left-4 md:-left-12 lg:-left-16 font-default text-[10rem] md:text-[14rem] lg:text-[18rem] leading-none text-primary/20 select-none pointer-events-none">
              &ldquo;
            </span>

            {}
            <blockquote className="relative font-default font-bold text-3xl md:text-5xl lg:text-[56px] leading-[1.15] text-foreground tracking-tighter">
              {words.map((word, i) => {
              const isHighlight = i >= highlightStart && i < highlightEnd;
              return <span key={`${activeIndex}-${i}`} data-index={i} className="inline-block transition-all duration-700 ease-out" style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(20px)",
                transitionDelay: `${i * 60}ms`,
                color: isHighlight ? "var(--primary)" : undefined
              }}>
                    {word}&nbsp;
                  </span>;
            })}
            </blockquote>

            {}
            <div className="mt-12 md:mt-16 flex items-center gap-5 transition-all duration-700 ease-out" style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(20px)",
            transitionDelay: `${words.length * 60 + 200}ms`
          }}>
              <SafeHydrate><Avatar className="size-16 border border-border">
                <AvatarImage src={active.avatar} alt={active.name} className="object-cover" />
                <AvatarFallback className="font-default text-base">
                  {active.name.charAt(0)}
                </AvatarFallback>
              </Avatar></SafeHydrate>
              <div className="flex flex-col gap-1">
                <span className="font-default text-lg text-foreground">
                  {active.name}
                </span>
                <span className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
                  — {active.role}
                </span>
              </div>
            </div>
          </div>

          {}
          <div className="hidden md:flex absolute top-1/2 -translate-y-1/2 left-0 col-span-1">
            <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="size-12 rounded-lg text-muted-foreground hover:text-primary hover:bg-accent">
              <ChevronLeft className="size-6" strokeWidth={1.25} />
            </Button>
          </div>
          <div className="hidden md:flex absolute top-1/2 -translate-y-1/2 right-0 col-span-1 justify-end">
            <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Next testimonial" className="size-12 rounded-lg text-muted-foreground hover:text-primary hover:bg-accent">
              <ChevronRight className="size-6" strokeWidth={1.25} />
            </Button>
          </div>
        </div>

        {}
        <div className="flex justify-center gap-3 mb-20 md:mb-28">
          {focalTestimonials.map((_, i) => <Button key={i} data-index={i} variant="ghost" onClick={() => setActiveIndex(i)} aria-label={`Go to testimonial ${i + 1}`} className="p-2 h-auto w-auto">
              <span className="block size-2 rounded-lg transition-all duration-300" style={{
            backgroundColor: i === activeIndex ? "var(--primary)" : "var(--muted-foreground)",
            opacity: i === activeIndex ? 1 : 0.3,
            width: i === activeIndex ? "1.5rem" : "0.5rem"
          }} />
            </Button>)}
        </div>

        {}
        <div className="flex md:hidden justify-center gap-4 mb-20">
          <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="size-12 rounded-lg border border-border text-muted-foreground hover:text-primary">
            <ChevronLeft className="size-5" strokeWidth={1.25} />
          </Button>
          <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Next testimonial" className="size-12 rounded-lg border border-border text-muted-foreground hover:text-primary">
            <ChevronRight className="size-5" strokeWidth={1.25} />
          </Button>
        </div>

        {}
        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {smallTestimonials.map((t, i) => {
          const colSpans = ["md:col-start-2 md:col-span-3", "md:col-start-5 md:col-span-4", "md:col-start-9 md:col-span-3"];
          return <div data-index={i} key={i} className={`col-span-12 ${colSpans[i]} border border-border bg-card p-7 md:p-8 transition-all duration-500 ease-out`} style={{
            borderRadius: "0rem",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(24px)",
            transitionDelay: `${800 + i * 150}ms`
          }}>
                <SafeHydrate><Avatar className="size-14 mb-5 border border-border">
                  <AvatarImage src={t.avatar} alt={t.name} className="object-cover" />
                  <AvatarFallback className="font-default">
                    {t.name.charAt(0)}
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <p className="font-default text-base md:text-lg leading-snug text-foreground mb-6">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="flex flex-col gap-1 pt-5 border-t border-border">
                  <span className="font-default text-sm text-foreground">
                    {t.name}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-primary">
                    — {t.role}
                  </span>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}